import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

interface TagsModalProps {
  photoId: string;
  onClose: () => void;
}

interface Tag {
  tag: string;
  type: 'person' | 'place' | 'thing';
  addedBy: string;
  createdAt: string;
}

interface TagWithCount extends Tag {
  count: number;
}

export default function TagsModal({ photoId, onClose }: TagsModalProps) {
  const queryClient = useQueryClient();
  const [newTag, setNewTag] = useState('');
  const [tagType, setTagType] = useState<'person' | 'place' | 'thing'>('person');
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  const { data: tagsData, isLoading } = useQuery({
    queryKey: ['tags', photoId],
    queryFn: () => api.listTags(photoId),
  });

  const tags: Tag[] = tagsData?.tags || [];

  // Fetch all existing tags for autocomplete
  const { data: allTagsData } = useQuery({
    queryKey: ['all-tags'],
    queryFn: () => api.listAllTags(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const existingTags: TagWithCount[] = allTagsData?.tags || [];

  const addMutation = useMutation({
    mutationFn: () => {
      // Normalize tag to lowercase for consistency
      const normalizedTag = newTag.trim().toLowerCase();
      return api.addTag(photoId, { tag: normalizedTag, type: tagType });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', photoId] });
      queryClient.invalidateQueries({ queryKey: ['all-tags'] }); // Refresh autocomplete data
      setNewTag('');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (tag: string) => api.removeTag(photoId, tag),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', photoId] });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newTag.trim()) {
      addMutation.mutate();
    }
  };

  const updateTagSuggestions = (value: string, type: 'person' | 'place' | 'thing') => {
    // Normalize input to lowercase for comparison
    const searchValue = value.toLowerCase().trim();
    
    if (searchValue.length > 0) {
      // Filter existing tags by current type and search value (case-insensitive)
      const matchingTags = existingTags
        .filter((t: TagWithCount) => {
          const tagLower = t.tag.toLowerCase();
          return t.type === type && 
            tagLower.includes(searchValue) &&
            tagLower !== searchValue; // Don't show exact match
        })
        .map((t: TagWithCount) => t.tag)
        .slice(0, 8); // Show max 8 suggestions
      
      setTagSuggestions(matchingTags);
      setShowTagSuggestions(matchingTags.length > 0);
    } else {
      // Show popular tags when field is empty
      const popularTags = existingTags
        .filter((t: TagWithCount) => t.type === type)
        .sort((a: TagWithCount, b: TagWithCount) => b.count - a.count) // Sort by usage count
        .map((t: TagWithCount) => t.tag)
        .slice(0, 8); // Show max 8 suggestions
      
      setTagSuggestions(popularTags);
      setShowTagSuggestions(popularTags.length > 0);
    }
  };

  const handleTagChange = (value: string) => {
    setNewTag(value);
    setSelectedSuggestionIndex(-1);
    updateTagSuggestions(value, tagType);
  };

  const handleTagTypeChange = (type: 'person' | 'place' | 'thing') => {
    setTagType(type);
    setSelectedSuggestionIndex(-1);
    // Update suggestions when tag type changes
    updateTagSuggestions(newTag, type);
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showTagSuggestions) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < tagSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      
      case 'Enter':
        if (selectedSuggestionIndex >= 0) {
          e.preventDefault();
          selectSuggestion(tagSuggestions[selectedSuggestionIndex]);
        }
        break;
      
      case 'Escape':
        setShowTagSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  const selectSuggestion = (tag: string) => {
    setNewTag(tag);
    setShowTagSuggestions(false);
    setSelectedSuggestionIndex(-1);
  };

  const groupedTags = {
    person: tags.filter((t) => t.type === 'person'),
    place: tags.filter((t) => t.type === 'place'),
    thing: tags.filter((t) => t.type === 'thing'),
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">🏷️ Tags</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Add Tag Form */}
          <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Add New Tag</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Step 1: Select Tag Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  1. Select Tag Type
                </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleTagTypeChange('person')}
                  className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                    tagType === 'person'
                      ? 'bg-primary-100 border-primary-500 text-primary-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  👤 Person
                </button>
                <button
                  type="button"
                  onClick={() => handleTagTypeChange('place')}
                  className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                    tagType === 'place'
                      ? 'bg-primary-100 border-primary-500 text-primary-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  📍 Place
                </button>
                <button
                  type="button"
                  onClick={() => handleTagTypeChange('thing')}
                  className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                    tagType === 'thing'
                      ? 'bg-primary-100 border-primary-500 text-primary-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  🎯 Thing
                </button>
              </div>
            </div>

            {/* Step 2: Enter Tag Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                2. Enter Tag Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => handleTagChange(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  onFocus={() => {
                    // Show popular tags when focusing on empty field
                    if (newTag.length === 0) {
                      updateTagSuggestions('', tagType);
                    }
                  }}
                  onBlur={() => {
                    // Delay to allow click on suggestion
                    setTimeout(() => setShowTagSuggestions(false), 200);
                  }}
                  placeholder="e.g., Summer, Paris, Birthday..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  autoComplete="off"
                />
                
                {/* Tag Suggestions Dropdown */}
                {showTagSuggestions && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto z-50">
                    {tagSuggestions.length === 0 ? (
                      <div className="px-4 py-2 text-sm text-gray-500">
                        No matching tags
                      </div>
                    ) : (
                      tagSuggestions.map((tag, index) => {
                        const tagData = existingTags.find((t: TagWithCount) => t.tag === tag);
                        const usageCount = tagData?.count || 0;
                        
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => selectSuggestion(tag)}
                            className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors flex items-center justify-between ${
                              index === selectedSuggestionIndex ? 'bg-gray-100' : ''
                            }`}
                          >
                            <span className="text-sm font-medium">{tag}</span>
                            <span className="text-xs text-gray-400">
                              {usageCount} photo{usageCount !== 1 ? 's' : ''}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Step 3: Add Tag */}
            <button
              type="submit"
              disabled={!newTag.trim() || addMutation.isPending}
              className="btn-primary w-full justify-center py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addMutation.isPending ? 'Adding...' : '✓ Add Tag'}
            </button>
          </form>
          </div>

          {/* Tags List */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Current Tags</h3>

            {isLoading ? (
              <div className="text-center py-4 text-gray-500">Loading...</div>
            ) : tags.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                No tags yet. Add the first one!
              </div>
            ) : (
              <>
                {Object.entries(groupedTags).map(([type, typeTags]) => {
                  if (typeTags.length === 0) return null;
                  
                  const icon = type === 'person' ? '👤' : type === 'place' ? '📍' : '🎯';
                  
                  return (
                    <div key={type} className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-600 capitalize">
                        {icon} {type}s
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {typeTags.map((tag) => (
                          <span
                            key={tag.tag}
                            className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                          >
                            {tag.tag}
                            <button
                              onClick={() => removeMutation.mutate(tag.tag)}
                              disabled={removeMutation.isPending}
                              className="text-gray-400 hover:text-red-600 transition-colors"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

